import { Component, ErrorInfo, PropsWithChildren, useState } from 'react';
import { OkDialog } from '../../../CommonControls/Dialog/OkDialog';
import { useLocalization } from '../../../Contexts/Localization';

interface ErrorBoundaryProps extends PropsWithChildren {
    /**
     * Callback when error is encountered
     *
     * @param errorObj basic error information
     * @param errorInfo component stack trac information
     * @returns
     */
    onError: (errorObj: ReactError, errorInfo?: ErrorInfo) => void;
}

/**
 * React doesn't export the type they use for the error object,
 * so I just redefined it.
 */
interface ReactError {
    name: string;
    message: string;
    stack?: string;
}

class ErrorBoundaryBase extends Component<ErrorBoundaryProps> {
    state = {
        hasError: false,
    };

    componentDidCatch(error: ReactError, errorInfo: ErrorInfo) {
        this.props.onError(error, errorInfo);
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    render() {
        return this.state.hasError ? null : this.props.children;
    }
}

/**
 * Catches errors thrown in the application and displays a user friendly dialog
 */
export const ErrorBoundary: React.FC<PropsWithChildren> = (props) => {
    const [error, setError] = useState<boolean>(false);
    const Localization = useLocalization();

    const onClose = () => {
        window.location.replace('/');
    };

    const onError = (_errorObj: ReactError, _errorInfo?: ErrorInfo) => {
        setError(true);
    };

    return (
        <>
            {error ?
                <OkDialog
                    open={error}
                    onClose={onClose}
                    title={Localization.AnErrorHasOccurred}
                >
                    {Localization.Apologize}
                    <br />
                    <br />
                    {Localization.ErrorRedirect}
                </OkDialog>
                :
                <ErrorBoundaryBase onError={onError}>
                    {props.children}
                </ErrorBoundaryBase>
            }
        </>
    );
};

/**
 * If something has gone terribly wrong and an error is thrown in our component tree above above the ErrorBoundary,
 * this screen will act as a fallback. We can't use any hooks (IE Localized strings) or components
 * since this will be rendered at the very top level of the application
 */
export const FallbackErrorBoundary: React.FC<PropsWithChildren> = (props) => {
    const [error, setError] = useState<boolean>(false);
    const [errorInfo, setErrorInfo] = useState<ErrorInfo | undefined>();
    const [errorObj, setErrorObj] = useState<ReactError | undefined>();

    const onError = (errorObj: ReactError, errorInfo?: ErrorInfo) => {
        setErrorInfo(errorInfo);
        setErrorObj(errorObj);
        setError(true);
    };

    return (
        <>
            {error ?
                <div style={{ margin: '1rem' }}>
                    <h1>{'Something went wrong'}</h1>
                    <details style={{ whiteSpace: 'pre-wrap' }}>
                        {errorObj?.toString()}
                        <br />
                        {errorInfo?.componentStack}
                    </details>
                </div>
                :
                <ErrorBoundaryBase onError={onError}>
                    {props.children}
                </ErrorBoundaryBase>
            }
        </>
    );
};